import { z } from '../zod';
import { CurrencyCodeSchema } from '../shared/currencyCode';
import {
  PricingTypeEnumValidationSchema,
  TaxBehaviourEnumValidationSchema,
  BadgeIconEnumValidationSchema,
  VariantSelectionTypeEnumValidationSchema,
  VariantStatusEnumValidationSchema,
} from './enum';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

export const RecurringValidationSchema = z.object({
  interval: z.enum(['day', 'week', 'month', 'year']).nullish().meta({
    description: 'Billing interval for subscriptions.',
    example: 'month',
  }),
  intervalCount: z.number().min(1).nullish().meta({
    description: 'Number of intervals between billings.',
    example: 1,
  }),
  trialPeriodDays: z.number().min(0).nullish().meta({
    description: 'Trial period in days for subscriptions.',
  }),
  billingCycleAnchorConfig: z
    .object({
      enabled: z.boolean().nullish(),
      dayOfMonth: z.number().min(1).max(31).nullish().meta({
        description:
          'Day of the month subscriptions renew on. Renewals happen at 00:00 UTC. Customers who subscribe between renewal days pay a prorated amount for their first period. Customers who subscribe in the first 24 hours of the renewal day (00:00 to 24:00 UTC) are backdated to 00:00 and pay the full price. The full-price rule applies to subscriptions billed every month or every year, not payment plans. Defaults to `1`.',
        example: 1,
      }),
      month: z.number().min(1).max(12).nullish(),
    })
    .nullish(),
  startDate: z.iso.datetime().nullish().meta({
    description: 'The date on which the subscription begins.',
    example: '2026-02-25T06:40:09.463Z',
  }),
});

export const PaymentPlanValidationSchema = RecurringValidationSchema.extend({
  planIterations: z.number().min(1).meta({
    description:
      'The number of times a subscription renews. After set number of renewals the payment plan is complete.',
    example: 3,
  }),
});

export const BasePriceInputValidationSchema = z.object({
  amount: z.number().min(0).nullish().meta({
    description: 'Price in smallest currency unit (cents). Defaults to `0`.',
    example: 4900,
  }),
  payWhatYouWant: z.boolean().nullish().meta({
    description:
      'If enabled `amount` field becomes the minimum price and customers can choose to pay what they want. If `amount` is empty or `0` customer can purchase the product for free. Pay What You Want applies to one-time prices and subscriptions, but not payment plans.',
  }),
  currency: CurrencyCodeSchema.nullish().meta({
    description: 'Currency code (lowercase). Defaults to `usd`.',
    example: 'usd',
  }),
  discountedFromPrice: z.number().min(0).nullish().meta({
    description: 'The original price of the product in the smallest currency unit (cents).',
    example: 9900,
  }),
  setupFee: z.number().min(0).nullish().meta({
    description: 'One-time setup fee in cents.',
  }),
  setupFeeMultipliesWithQuantity: z.boolean().nullish().meta({
    description:
      'Whether or not the setup fee is multipled by the selected quantity. Applies to `one-time` and `pwyw` only. Defaults to `false`.',
    example: false,
  }),
  recurring: RecurringValidationSchema.nullish().meta({
    description: 'The recurring components of a price such as `interval` and `intervalCount`',
  }),
  paymentPlan: PaymentPlanValidationSchema.nullish().meta({
    description: 'The payment plan components of a price such as `interval` and `intervalCount`',
  }),
});

const priceEntryInputFields = {
  id: z.string().nullish().meta({
    description: 'Existing price id (omit when creating a new price).',
  }),
  label: z.string().nullish().meta({
    description: 'Optional seller-facing label for this price (e.g. "Monthly", "Annual").',
  }),
  badge: z.string().nullish().meta({
    description:
      'Optional short label shown over the price card (e.g. "Save $50", "Most popular").',
  }),
  badgeIcon: BadgeIconEnumValidationSchema.nullish().meta({
    description: 'Optional icon shown before the badge text. `null` removes it.',
  }),
  reference: z.string().nullish().meta({
    description:
      "Stable label for this price, sent to exports, Zapier, Google Sheets and webhooks as `priceReference`. Each sale keeps the reference it was bought with, so changing it gives the same price two names in the seller's data. Generated when omitted, and a price resent by id keeps its stored reference. Only send it when the seller asks to set or change the reference: never to match a new amount or name. Don't put an amount in it, since a coupon or a later price change makes the number wrong.",
    example: 'complete-bundle',
  }),
  order: z.number().nullish().meta({
    description: 'Dashboard display order. Lower numbers appear first.',
  }),
  enabled: z.boolean().nullish().meta({
    description:
      'Whether this price is selectable by customers. Defaults to `true`. At least one price must remain enabled.',
  }),
  hidden: z.boolean().nullish().meta({
    description: 'Hide this price. Hidden prices can still be shown using query parameters.',
  }),
  pwywSuggestedPrice: z.number().min(0).nullish().meta({
    description: 'Suggested price in cents shown to customers when `payWhatYouWant` is `true`.',
    example: 2500,
  }),
};

export const PriceInputValidationSchema = BasePriceInputValidationSchema.extend({
  ...priceEntryInputFields,
  billingType: z.enum(['one_time', 'recurring']).meta({
    description:
      'Billing type. Part of the pricing-type identity fixed at creation — cannot change on an existing price.',
    example: 'one_time',
  }),
  // Multi-price requires an explicit amount (the `price` alias defaults it).
  amount: z.number().min(0).meta({
    description: 'Price in smallest currency unit (cents).',
    example: 4900,
  }),
})
  .refine(p => !(p.recurring && p.paymentPlan), {
    message: 'A price cannot have both recurring and paymentPlan configs.',
    path: ['paymentPlan'],
  })
  .refine(p => p.billingType !== 'one_time' || (!p.recurring && !p.paymentPlan), {
    message: 'one_time prices cannot have a recurring or paymentPlan config.',
    path: ['recurring'],
  })
  .refine(
    p => {
      if (p.billingType !== 'recurring') return true;
      const config = p.recurring ?? p.paymentPlan;
      return config != null && config.interval != null && config.intervalCount != null;
    },
    {
      message:
        'recurring prices require a recurring or paymentPlan config with interval and intervalCount.',
      path: ['recurring'],
    },
  )
  // PWYW: allowed for one-time prices and open-ended subscriptions; still
  // blocked for payment plans (identified by the paymentPlan config, which
  // carries planIterations). Enablement point for payment-plan PWYW (future):
  // relax this to also permit p.paymentPlan.
  .refine(p => !p.payWhatYouWant || !p.paymentPlan, {
    message: 'payWhatYouWant cannot be used with payment plans.',
    path: ['payWhatYouWant'],
  });

// Unrefined object shape of the `price` alias — kept separate because Zod 4
// forbids .partial() on refined schemas (mirrors the api copy).
export const SinglePriceInputObjectSchema = BasePriceInputValidationSchema.extend({
  ...priceEntryInputFields,
  billingType: z
    .enum(['one_time', 'recurring'])
    .nullish()
    .meta({
      description:
        'Billing type. Derived when omitted: `recurring`/`paymentPlan` present → recurring, else one_time. ' +
        'Part of the pricing-type identity fixed at creation — cannot change on an existing price.',
    }),
  pricingType: PricingTypeEnumValidationSchema.nullish().meta({
    description: 'Deprecated — superseded by `prices[]`. Accepted and ignored.',
    example: 'single',
    deprecated: true,
  }),
});

// The `price` ALIAS — same shape as a `prices[]` entry with create-compat
// relaxations: billingType derived when omitted, amount defaults to 0.
export const SinglePriceInputValidationSchema = SinglePriceInputObjectSchema.refine(
  p => !(p.recurring && p.paymentPlan),
  {
    message: 'A price cannot have both recurring and paymentPlan configs.',
    path: ['paymentPlan'],
  },
)
  .refine(p => p.billingType !== 'one_time' || (!p.recurring && !p.paymentPlan), {
    message: 'one_time prices cannot have a recurring or paymentPlan config.',
    path: ['recurring'],
  })
  .refine(p => !(p.billingType === 'recurring' && !p.recurring && !p.paymentPlan), {
    message: 'recurring prices require a recurring or paymentPlan config.',
    path: ['recurring'],
  })
  // PWYW: one-time prices and open-ended subscriptions; still blocked for
  // payment plans. Enablement point for payment-plan PWYW (future): drop this.
  .refine(p => !p.payWhatYouWant || !p.paymentPlan, {
    message: 'payWhatYouWant cannot be used with payment plans.',
    path: ['payWhatYouWant'],
  });

export const VariantOptionValidationRequestSchema = z.object({
  key: z.string().nullish().meta({
    description:
      'Optional caller-defined key for this option. Not persisted to the database. Required when `preselect.optionId` on the same variant, `showHideLogic.value` on another variant, or a `discounts[].variantOptionIds[].key` entry needs to reference this option in the same request.',
    example: 'pro-option',
  }),
  name: z.string().meta({
    description: 'Name of the variant option.',
    example: 'Basic',
  }),
  description: z.string().nullish().meta({
    description: 'Description of the variant option. Supports HTML or plain text.',
  }),
  status: VariantStatusEnumValidationSchema.nullish().meta({
    description: 'Status of the variant option. Defaults to `enabled`.',
    example: 'enabled',
  }),
  imageId: ObjectIdStringValidationSchema.nullish(),
  additionalChargeAmount: z.number().nullish().meta({
    description: 'The additional charge for selecting this variant option in cents.',
    example: 1000,
  }),
  discountedFromPrice: z.number().min(0).nullish().meta({
    description: 'The original price of this variant option in cents.',
    example: 1500,
  }),
  sku: z.string().nullish().meta({
    description: 'SKU for this option.',
  }),
  stock: z.number().nullish().meta({
    description: 'Stock quantity for this option.',
    example: 10,
  }),
  type: z.enum(['one_time', 'recurring']).nullish().meta({
    description:
      'Type of variant option - one-time purchase or recurring subscription. For Pay What You Want options, `type` must be `one_time` and set `payWhatYouWant: true`.',
    example: 'one_time',
  }),
  payWhatYouWant: z.boolean().nullish().meta({
    description:
      'If enabled, customers can choose their own price for this variant option. The minimum price is based on the additionalChargeAmount. This is only supported for `one_time` options. Defaults to `false`.',
    example: false,
  }),
  pwywSuggestedPrice: z.number().min(0).nullish().meta({
    description:
      'Suggested price for Pay What You Want options in cents. This is a cosmetic field used to display a suggested amount to the customer. Defaults to `0`.',
    example: 2500,
  }),
  fileIds: z.array(ObjectIdStringValidationSchema).nullish().meta({
    description: 'Files specific to this variant option. Available to the customer after purchase.',
  }),
});

export const VariantRequestValidationSchema = z.object({
  key: z.string().nullish().meta({
    description:
      'Optional caller-defined key for this variant. Not persisted to the database. Required when other variants reference this variant in `showHideLogic.element.elementId`. Bulk discounts with `variant_option_quantity` use option-level keys from `variants[].options[].key`.',
    example: 'select-package-tier',
  }),
  name: z.string().meta({
    description: 'Name of the variant.',
    example: 'Plan Type',
  }),
  required: z.boolean().nullish().meta({
    description: 'Whether the customer must select at least one option in the variant.',
    example: true,
  }),
  status: VariantStatusEnumValidationSchema.nullish().meta({
    description: 'Status of the variant. Defaults to `enabled`',
    example: 'enabled',
  }),
  options: z.array(VariantOptionValidationRequestSchema).min(1).meta({
    description: 'Options within this variant. Variant must have at least one option.',
  }),
  order: z.number().nullish().meta({
    description: 'Display order of the variant. Defaults to `0`.',
    example: 0,
  }),
  reference: z.string().max(36).nullish().meta({
    description:
      'Stable ID that exports and integrations use for this variant. Generated from the variant name when omitted, and not updated when the name changes. Changing it breaks integrations that match on the old one, so only send it when the seller asks.',
    example: 'fd3136f8-da1f-4a4c-b381-32a097b60c38',
  }),
  selectionType: VariantSelectionTypeEnumValidationSchema.nullish().meta({
    description:
      'Type of selection. If `single` the customer can choose one option in the variant group. If `multiple` the customer can choose multiple options in the variant group. If `quantity` the customer can select multiple options and set a quantity of each variant in the variant group. Defaults to `single`',
    example: 'single',
  }),
  increasesWithQuantity: z.boolean().nullish().meta({
    description:
      'Whether or not the variant quantity matches the checkout quantity. Defaults to `false`.',
  }),
  useVariantOptionSkus: z.boolean().nullish().meta({
    description:
      'If enabled, use SKUs from variant options instead of the product SKU. Defaults to `false`.',
  }),
  manageVariantOptionStock: z.boolean().nullish().meta({
    description: 'If enabled stock is managed by the variant options. Defaults to `false`.',
  }),
  priceIds: z
    .array(ObjectIdStringValidationSchema)
    .nullish()
    .meta({
      description:
        'Price IDs this variant group is scoped to. Required when the product has multiple prices configured via `prices[]`. Customers will only see this variant group when the scoped price is selected.',
      example: ['507f1f77bcf86cd799439011'],
    }),
  hidden: z.boolean().nullish().meta({
    description:
      'Whether this variant is hidden. Hidden variants can be shown or filled with data by query paramers. [Learn more](https://checkoutpage.com/docs/articles/preselect-checkout-variants). Defaults to `false`.',
  }),
  preselect: z
    .object({
      enabled: z.boolean().nullish().meta({
        description: 'Enable preselecting a variant option. Defaults to `false`.',
      }),
      optionId: z.string().nullish().meta({
        description:
          'The `key` of the variant option to preselect. Must match the `key` of an option on this variant.',
        example: 'pro-option',
      }),
      quantity: z.number().nullish().meta({
        description:
          'Quantity to preselect for quantity selection type variants. Defaults to `1` for `single` and `multiple` selection types.',
        example: 2,
      }),
    })
    .nullish()
    .meta({
      description: 'Preselect configuration for this variant.',
      example: {
        enabled: true,
        optionId: 'pro-option',
        quantity: 1,
      },
    }),
  showHideLogic: z
    .object({
      enabled: z.boolean().nullish().meta({
        description: 'Enable conditional show/hide logic. Defaults to `false`.',
        example: true,
      }),
      comparison: z.enum(['is', 'is_not', 'is_empty', 'is_not_empty', 'contains']).nullish().meta({
        description:
          'Comparison operator for the condition. Defaults to `is`. Use `value` with `is`, `is_not`, and `contains`. Omit `value` for `is_empty` and `is_not_empty`.',
        example: 'is',
      }),
      value: z.string().nullish().meta({
        description:
          'The `key` of the variant option being referenced. Must match the `key` of an option on the source variant identified by `showHideLogic.element.elementId`. Use this with `is`, `is_not`, and `contains`. Omit this field for `is_empty` and `is_not_empty`.',
        example: 'pro-option',
      }),
      element: z
        .object({
          elementId: z.string().nullish().meta({
            description:
              'The `key` of the source variant used in the condition. Must match the `key` of another variant in the same batch.',
            example: 'select-package-tier',
          }),
        })
        .nullish()
        .meta({
          description: 'Element (variant) to compare against.',
        }),
    })
    .nullish()
    .meta({
      description: 'Conditional logic to show or hide this variant based on other form elements.',
    }),
  layout: z
    .object({
      variantOptionLayout: z.enum(['list', 'grid']).nullish().meta({
        description: 'Layout style for variant options. Defaults to `list`.',
        example: 'list',
      }),
      collapseVariantOptions: z.boolean().nullish().meta({
        description: 'If enabled, variant options are collapsed by default. Defaults to `false`.',
        example: false,
      }),
      variantOptionColumns: z.number().min(1).max(10).nullish().meta({
        description: 'Number of columns for grid layout. Defaults to `2`.',
        example: 2,
      }),
      showVariantName: z.boolean().nullish().meta({
        description: 'Show the variant name. Defaults to `true`.',
        example: true,
      }),
      showVariantOptionNames: z.boolean().nullish().meta({
        description: 'Show variant option names. Defaults to `true`.',
        example: true,
      }),
      showVariantOptionPrices: z.boolean().nullish().meta({
        description: 'Show variant option prices. Defaults to `true`.',
        example: true,
      }),
      showVariantOptionPriceSign: z.boolean().nullish().meta({
        description: 'Show price sign (+ or -) for variant option prices. Defaults to `false`.',
        example: true,
      }),
      showPwywPlaceholder: z.boolean().nullish().meta({
        description: 'Show placeholder text for Pay What You Want inputs. Defaults to `true`.',
        example: true,
      }),
      textAlign: z.enum(['left', 'center', 'stack', '']).nullish().meta({
        description: 'Text alignment for variant options. Defaults to `left`.',
        example: 'left',
      }),
      spacing: z.enum(['comfortable', 'compact', '']).nullish().meta({
        description: 'Spacing between variant options. Defaults to `comfortable`.',
        example: 'comfortable',
      }),
      imageSize: z.enum(['small', 'medium', 'large', '']).nullish().meta({
        description: 'Size of variant option images. Defaults to `small`.',
        example: 'small',
      }),
    })
    .nullish()
    .meta({
      description: 'Layout configuration for this variant.',
    }),
});

export const ProductDetailsInputValidationSchema = z
  .object({
    title: z.string().max(200).nullish().meta({
      description: 'Product title. Defaults to page name if not provided.',
      example: 'Premium Course',
    }),
    description: z.string().nullish().meta({
      description: 'Description of the product. Accepts HTML or plain text.',
      example:
        'Join our premium course today learn more at <a href="https://checkoutpage.com">Checkout Page</a>.',
    }),
    price: SinglePriceInputValidationSchema.optional().meta({
      description:
        'The singular way to set a product price — equivalent to `prices: [entry]` and materialized as one. ' +
        'When `prices` is also provided it takes precedence and `price` is ignored. ' +
        'On update, `price` is only valid while the product has at most one price (a legacy product transitions to `prices[]` on first use); ' +
        'products with multiple prices must be updated through `prices[]`.',
    }),
    sku: z.string().max(200).nullish().meta({
      description: 'SKU of the product.',
    }),
    hasUnlimitedStock: z.boolean().nullish().meta({
      description:
        'Whether or not the stock of the product is unlimited. If set to `true`, the `stock` field is ignored.',
      example: false,
    }),
    stock: z.number().min(0).nullish().meta({
      description: 'Available stock of the product.',
      example: 100,
    }),
    taxBehavior: TaxBehaviourEnumValidationSchema.nullish().meta({
      description:
        'If Stripe Tax is enabled, determines the behaviour of Stripe Tax. Defaults to your Stripe Tax settings.',
      example: '',
    }),
    // Stripe tax code (txcd_…) or '' to use the account default; reject anything else
    // up front so a bad code doesn't fail the whole product save when Stripe rejects it.
    taxCode: z
      .string()
      .regex(
        /^(txcd_[a-zA-Z0-9]+)?$/,
        'Tax code must be a valid Stripe tax code (e.g. txcd_10000000)',
      )
      .nullish()
      .meta({
        description:
          'If Stripe Tax is enabled, determines the tax code used when calculating tax. Defaults to your Stripe Tax settings.',
      }),
    fixedTaxRateIds: z.array(ObjectIdStringValidationSchema).max(1).nullish().meta({
      description:
        "List of Tax Rate IDs applied to this product. When the page's `tax.mode` is `fixed` and this is omitted on create, the seller default rate is applied. Call `list_tax_rates` first to find an existing rate; use `create_tax_rate` to add a new one.",
    }),
    imageIds: z
      .array(ObjectIdStringValidationSchema)
      .nullish()
      .meta({
        description:
          'Images for the product. Provide a list of image file IDs. Use the `/files/upload` API to add images.',
        example: ['507f1f77bcf86cd799439011'],
      }),
    fileIds: z
      .array(ObjectIdStringValidationSchema)
      .optional()
      .meta({
        description: 'Files for the product. Available to the customer after purchase.',
        example: ['507f1f77bcf86cd799439011'],
      }),
    variantsRequired: z.boolean().nullish().meta({
      description:
        'Use when no variants are marked as required but you still need the customer to select at least one variant option. Ignored when any variant is individually required. Defaults to `false`.',
      example: false,
    }),
    variants: z.array(VariantRequestValidationSchema).nullish().meta({
      description:
        'Product variants and options. To link a discount to specific variant options, assign a `key` to each relevant option here and reference those keys in `discounts[].variantOptionIds[].key` within the same request.',
    }),
    discounts: z
      .array(
        z.object({
          quantityCondition: z.enum(['checkout_quantity', 'variant_option_quantity']).meta({
            description:
              'How this bulk discount is triggered. Use `checkout_quantity` to apply the discount based on the total checkout quantity. Use `variant_option_quantity` only when this same request also includes `variants` and matching option `key` values referenced by `variantOptionIds`. Defaults to `checkout_quantity`.',
            example: 'checkout_quantity',
          }),
          variantOptionIds: z
            .array(z.object({ key: z.string() }))
            .nullish()
            .meta({
              description:
                'Variant option keys that this discount applies to. Only use this when `quantityCondition` is `variant_option_quantity`. Each entry must exactly match a temporary `key` from `variants[].options[].key` in the same request. These keys are caller-defined handles that are resolved to MongoDB ObjectIds at save time and do not appear in the response.',
            }),
          minQuantity: z.number().min(1).nullish().meta({
            description: 'Minimum quantity required for this discount to apply.',
            example: 5,
          }),
          maxQuantity: z.number().min(1).nullish().meta({
            description: 'Maximum quantity for this discount tier. Leave empty for no upper limit.',
            example: 10,
          }),
          percentOff: z.number().min(0).max(100).nullish().meta({
            description: 'Percentage discount to apply. Cannot be used with `amountOff`.',
            example: 10,
          }),
          amountOff: z.number().min(0).nullish().meta({
            description: 'Fixed amount discount in cents. Cannot be used with `percentOff`.',
            example: 500,
          }),
        }),
      )
      .nullish()
      .meta({
        description:
          'Bulk discounts for the product.\n\n' +
          'Use `checkout_quantity` when the discount should apply to the total checkout quantity.\n\n' +
          'Use `variant_option_quantity` when the discount should apply only to specific variant options. In that mode, you must do all of the following in the same request:\n' +
          '1. Include `productData.variants`.\n' +
          '2. Give each target variant option a temporary `key` in `variants[].options[].key`.\n' +
          '3. Reference those exact keys in `discounts[].variantOptionIds[].key`.\n\n' +
          '- `variants[].options`: [{ name: "Red", key: "red" }, { name: "Blue", key: "blue" }]\n' +
          '- `discounts`: [{ quantityCondition: "variant_option_quantity", variantOptionIds: [{ key: "red" }, { key: "blue" }], minQuantity: 3, percentOff: 10 }]\n\n' +
          '[Learn more](https://checkoutpage.com/docs/articles/bulk-discounts)',
        example: [
          {
            quantityCondition: 'variant_option_quantity',
            variantOptionIds: [{ key: 'red' }, { key: 'blue' }],
            minQuantity: 3,
            percentOff: 10,
          },
        ],
      }),
    limitSubscriptions: z
      .object({
        enabled: z.boolean().nullish().meta({
          description: 'Enable subscription limits. Defaults to `false`.',
        }),
        limitSubscriptionsStripe: z
          .object({
            enabled: z.boolean().nullish().meta({
              description:
                'Enable Stripe-level subscription limits (checks active subscriptions in Stripe). Defaults to `false`.',
            }),
          })
          .nullish()
          .meta({
            description: 'Stripe-specific subscription limit configuration.',
          }),
      })
      .nullish()
      .meta({
        description:
          'Configuration to limit the number of active subscriptions for this product. Used to cap subscriptions to a specific number.',
      }),
    limitPayments: z
      .object({
        enabled: z.boolean().nullish().meta({
          description:
            'Prevent a customer who has already paid for this product from paying again. One-time payments only. Defaults to `false`.',
        }),
      })
      .nullish()
      .meta({
        description:
          'Duplicate-payment prevention settings for this product. Applies to one-time payments only.',
      }),
    enableFileAccessForInactiveSubscriptions: z.boolean().nullish().meta({
      description: 'Allow customers with inactive subscriptions to keep accessing file downloads.',
      example: false,
    }),
    generateLicenseKeys: z.boolean().nullish().meta({
      description: 'If true, on purchase a license key for the product is generated',
      example: false,
    }),
    prices: z
      .array(PriceInputValidationSchema)
      .optional()
      .meta({
        description:
          'Explicit price list for this product. When provided alongside variants, use `variant.priceIds` to scope each variant group to its price. At least one entry must be enabled. ' +
          'When present, `prices[]` takes precedence over the legacy `price` field for determining what is shown at checkout.\n\n' +
          'Example — one-time + monthly subscription:\n' +
          '```json\n' +
          '[\n' +
          '  { "billingType": "one_time", "amount": 4900, "currency": "usd", "label": "One-time" },\n' +
          '  { "billingType": "recurring", "amount": 1000, "currency": "usd", "label": "Monthly", "recurring": { "interval": "month", "intervalCount": 1 } }\n' +
          ']\n' +
          '```',
        example: [
          { billingType: 'one_time', amount: 4900, currency: 'usd', label: 'One-time' },
          {
            billingType: 'recurring',
            amount: 1000,
            currency: 'usd',
            label: 'Monthly',
            recurring: { interval: 'month', intervalCount: 1 },
          },
        ],
      }),
    defaultPriceId: z.string().nullish().meta({
      description:
        'ID of the price pre-selected at checkout. Must reference an existing price `id` in `prices[]`. When omitted and there are several prices, checkout opens with none selected so the buyer chooses one.',
    }),
    pricePicker: z
      .object({
        title: z.string().nullish().meta({ description: 'Heading shown above the price options.' }),
        description: z.string().nullish().meta({
          description:
            'Text shown under the heading, above the price options. Line breaks are kept.',
        }),
        shortenDescription: z.boolean().nullish().meta({
          description:
            'Cut long price descriptions to their first lines behind a Show more link. Defaults to `true`.',
        }),
        priceDisplayLabels: z.record(z.string(), z.record(z.string(), z.string())).nullish().meta({
          description:
            'Per-axis label overrides for price tabs. Keyed by grouping axis (`billing` or `interval`), then by derived group name. E.g. `{ interval: { "One-time": "Lifetime" } }`.',
        }),
        priceDisplayDefaults: z.record(z.string(), z.string()).nullish().meta({
          description:
            'Per-axis default tab selection. Keyed by grouping axis. E.g. `{ interval: "Monthly" }`.',
        }),
        priceDisplayTabOrder: z.record(z.string(), z.array(z.string())).nullish().meta({
          description:
            'Per-axis tab display order. Keyed by grouping axis, value is ordered array of group names. E.g. `{ interval: ["Monthly", "Yearly"] }`.',
        }),
      })
      .nullish()
      .meta({
        description:
          'Price picker settings: the heading and description above the options, Show more / less, and tab labels, defaults and order.',
      }),
  })
  .refine(data => !(data.price?.recurring && data.price?.paymentPlan), {
    message:
      "Cannot specify both 'recurring' and 'paymentPlan'. Use 'recurring' for subscriptions or 'paymentPlan' for payment plans.",
    path: ['price'],
  })
  // PWYW: one-time prices and open-ended subscriptions; still blocked for
  // payment plans. Enablement point for payment-plan PWYW (future): drop the
  // paymentPlan check.
  .refine(data => !(data.price?.payWhatYouWant === true && data.price?.paymentPlan), {
    message: 'Pay What You Want does not apply to payment plans.',
    path: ['price', 'payWhatYouWant'],
  })
  .refine(d => !d.prices || d.prices.length >= 1, {
    message: 'prices must contain at least one entry.',
    path: ['prices'],
  })
  .refine(d => !d.prices || d.prices.some(p => p.enabled !== false), {
    message: 'At least one price must be enabled.',
    path: ['prices'],
  });
